import type { CSSProperties } from 'react'
import { useMemo } from 'react'

import { floorMs } from '@/utils/floor-ms'

import type { TodDisplayFormat } from '~shared/rundown/rundown'
import { formatTimeOfDay } from '~shared/timeutils'
import type { Timestamp } from '~shared/utils'

type StartTimeTopDisplayProps = {
  timestamp?: Timestamp | null
  timezone?: string
  todDisplayFormat?: TodDisplayFormat | null
  cellStyle?: CSSProperties
}

/**
 * StartTimeTopDisplay Component
 *
 * Displays the original/planned start time along with visual indicators
 * for hard start and auto-start modes.
 */
export function StartTimeTopDisplay({
  timestamp,
  timezone,
  todDisplayFormat,
  cellStyle,
}: StartTimeTopDisplayProps) {
  // Generate formatted time text internally based on timestamp
  const timeText = useMemo(() => {
    if (!timestamp) return ''

    // Only show original time if it differs from actual
    const originalTime = floorMs(timestamp.original.start)
    const actualTime = floorMs(timestamp.actual.start)

    if (originalTime.getTime() !== actualTime.getTime()) {
      return formatTimeOfDay(timestamp.original.start, {
        timezone,
        seconds: 'nonzero',
        format: todDisplayFormat,
      })
    }

    return ''
  }, [timestamp, timezone, todDisplayFormat])

  // If we have no time to display and no indicators to show, return null
  if (!timeText) return null

  return (
    <div
      className="h-4 text-xs text-white/60 tabular-nums space-x-2 whitespace-nowrap truncate px-1"
      style={cellStyle}
    >
      <span className="line-through">{timeText}</span>
    </div>
  )
}
